import LoraRuntimeAPI, {
  CharacterProfileDetail,
  ReferenceImageUpload,
  TrainingDataset,
} from './loraRuntime';
import DatasetsAPI from './datasets';
import { slugifyName } from './generatorProfiles';
import { referenceUploadFromUrl } from './characterIdentity';
import MediaAPI, { MediaLibraryItem } from './media';
import GenerateAPI from './generate';
import { constructMediaUrl } from './helpers';

/** Subject-token prefix for dataset-studio influencer profiles. */
const INFLUENCER_TOKEN_PREFIX = 'cg_inf';

/** Builds a deterministic, prompt-safe subject token from an influencer name. */
export const influencerSubjectToken = (name: string): string => {
  const slug = slugifyName(name).replace(/-/g, '_') || 'influencer';
  return `${INFLUENCER_TOKEN_PREFIX}_${slug}`.slice(0, 48);
};

/** A media library item's best display url, normalized for fetching. */
export const mediaItemDisplayUrl = (item: MediaLibraryItem): string =>
  item.url || item.preview_url || item.playback_url || '';

/**
 * Packages selected media-library items as base64 reference uploads the bridge
 * expects. Reuses characterIdentity's download helper. Items that cannot be
 * fetched are silently dropped.
 */
export const referenceUploadsFromMediaItems = async (
  items: MediaLibraryItem[]
): Promise<ReferenceImageUpload[]> => {
  const uploads = await Promise.all(
    items.map((item) => referenceUploadFromUrl(mediaItemDisplayUrl(item), item.name || 'reference'))
  );
  return uploads.filter((upload): upload is ReferenceImageUpload => upload !== null);
};

/**
 * Creates a character-lab profile for the named influencer, seeded with the
 * supplied reference images. The dataset run binds to the returned profile id so
 * approved review images can be uploaded back as approved references.
 */
export const createInfluencerProfile = async (
  name: string,
  referenceItems: MediaLibraryItem[],
  stylePrompt?: string
): Promise<CharacterProfileDetail> => {
  const reference_images = await referenceUploadsFromMediaItems(referenceItems);
  return LoraRuntimeAPI.createCharacterProfile({
    name,
    subject_token: influencerSubjectToken(name),
    style_prompt: stylePrompt || `${name} influencer reference`,
    reference_images,
  });
};

/** The fields of a review image the approve path needs to attach a reference. */
export interface ApprovableImage {
  media_id?: string;
  url?: string;
}

/** Options for {@link approveReviewImageToProfile}. */
export interface ApproveReviewImageOptions {
  /**
   * When true, attaches the image as a reference only — no trainable dataset
   * item is appended. Pass this for reference-collage-generator runs, whose
   * approved collages become the character's references, not its dataset.
   */
  skipDatasetItem?: boolean;
}

/**
 * Attaches one generated review image to the bound lab character. Sends the
 * run-level `media_id` (with the display url as a fallback) to the server-side
 * from-media endpoint, which resolves + downloads the bytes server-side —
 * avoiding the browser CORS failure that a direct `fetch()` of a signed url
 * hits. By default the backend appends the image as one item to the
 * character's persistent TrainingDataset and returns it, so the studio counter
 * can read the dataset's authoritative `image_count` (what training actually
 * reads). Pass `skipDatasetItem` for a collage run so the image attaches as a
 * reference only.
 */
export const approveReviewImageToProfile = async (
  characterId: string,
  image: ApprovableImage,
  options?: ApproveReviewImageOptions
): Promise<TrainingDataset> => {
  const fallbackUrl = image.url ? constructMediaUrl(image.url) : undefined;
  const mediaId = image.media_id || fallbackUrl;
  if (!mediaId) {
    throw new Error('Could not approve this image: it has no media id or url.');
  }
  return DatasetsAPI.addCharacterReferenceFromMedia(characterId, {
    media_id: mediaId,
    url: fallbackUrl,
    skip_dataset_item: options?.skipDatasetItem || undefined,
  });
};

/** Image count of the bound TrainingDataset — the count training reads. */
export const datasetImageCount = (dataset: TrainingDataset | null): number =>
  dataset ? dataset.image_count : 0;

/** Fetches the freshest profile detail for the bound influencer. */
export const refreshInfluencerProfile = (
  characterId: string
): Promise<CharacterProfileDetail> => LoraRuntimeAPI.getCharacterProfile(characterId);

/** A character (= one persistent dataset) plus its bound dataset image count. */
export interface CharacterSummary {
  id: string;
  name: string;
  /** Persistent dataset bound to this character, when one exists. */
  datasetId: string | null;
  /** Image count of the bound TrainingDataset, or 0 when none is bound yet. */
  imageCount: number;
}

/**
 * Lists the lab characters and joins each bound dataset's image_count from one
 * shared dataset index. Callers that already need the dataset list may pass that
 * request so Dataset Studio performs exactly two requests regardless of the
 * number of characters. Missing dataset data degrades counts to zero.
 */
export const listCharacterSummaries = async (
  datasets: TrainingDataset[] | Promise<TrainingDataset[]> = LoraRuntimeAPI.listDatasets()
): Promise<CharacterSummary[]> => {
  // Resolve characters and the already-needed dataset index in parallel. The
  // previous implementation fetched every bound dataset separately (N+1), even
  // though Dataset Studio also loads the complete dataset list for its sidebar.
  const [characters, allDatasets] = await Promise.all([
    LoraRuntimeAPI.listCharacters(),
    // Character names remain useful even if the dataset index is temporarily
    // unavailable; counts degrade to zero instead of hiding the whole list.
    Promise.resolve(datasets).catch(() => [] as TrainingDataset[]),
  ]);
  const imageCountByDatasetId = new Map(
    (allDatasets || []).map((dataset) => [dataset.id, datasetImageCount(dataset)])
  );
  return characters.map((character) => ({
    id: character.id,
    name: character.name,
    datasetId: character.dataset_id || null,
    imageCount: character.dataset_id
      ? imageCountByDatasetId.get(character.dataset_id) ?? 0
      : 0,
  }));
};

/**
 * Resolves the bound TrainingDataset for an influencer lab character via its
 * `profile.dataset_id`, or null when no dataset has been bound yet (no approvals
 * recorded). Drives the studio counter + curation view off the dataset training
 * actually reads, not the disjoint `reference_image_refs`.
 */
export const fetchBoundDataset = async (
  characterId: string
): Promise<TrainingDataset | null> => {
  const profile = await LoraRuntimeAPI.getCharacterProfile(characterId);
  if (!profile.dataset_id) return null;
  return LoraRuntimeAPI.getDataset(profile.dataset_id);
};

/** Normalized, fetchable url for a media-library reference selection. */
export const referenceMediaUrl = (item: MediaLibraryItem): string =>
  constructMediaUrl(mediaItemDisplayUrl(item));

/** A freshly generated replacement image: its media id and ready display url. */
export interface GeneratedReplacement {
  media_id: string;
  url: string;
}

const isReadyFileUrl = (fileUrl: unknown): fileUrl is string =>
  typeof fileUrl === 'string' &&
  fileUrl.length > 0 &&
  !fileUrl.includes('_waiting') &&
  !fileUrl.includes('_failed');

/**
 * Extracts a legible failure reason from a polled media item that errored out,
 * so the review card can show why a regenerate failed (e.g. a provider safety
 * rejection) instead of a generic string. Looks at the common top-level error
 * fields and the metadata bag, falling back to a generic message.
 */
const mediaFailureReason = (item: Record<string, unknown>): string => {
  const metadata = (item.metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    item.error,
    item.error_message,
    item.failure_reason,
    metadata.error,
    metadata.error_message,
    metadata.failure_reason,
    metadata.reason,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return 'Replacement image generation failed.';
};

/** The image generator + model a dataset run produces its images with. */
export interface DatasetImageGeneratorConfig {
  generator?: string;
  model?: string;
}

/**
 * Options for {@link generateReplacementImage}. `generator`/`model` MUST mirror
 * the dataset run's image-generate checkpoint so registry pricing resolves
 * (otherwise the backend returns `pricing_unavailable`). `referenceMediaIds`
 * are the approved/uploaded identity references for img2img conditioning.
 */
export interface GenerateReplacementOptions extends DatasetImageGeneratorConfig {
  referenceMediaIds?: string[];
  pollIntervalMs?: number;
  maxAttempts?: number;
}

/**
 * Generates a single standalone image from a prompt and polls until the media
 * file resolves to a ready url. Used by the review grid to mint a replacement
 * for a regenerated dataset image. Throws if generation fails or times out.
 */
export const generateReplacementImage = async (
  prompt: string,
  options: GenerateReplacementOptions = {}
): Promise<GeneratedReplacement> => {
  const pollIntervalMs = options.pollIntervalMs ?? 2000;
  const maxAttempts = options.maxAttempts ?? 60;

  const response = await GenerateAPI.generateMedia({
    type: 'image',
    prompt,
    generator: options.generator,
    model: options.model,
    reference_media_ids:
      options.referenceMediaIds && options.referenceMediaIds.length > 0
        ? options.referenceMediaIds
        : undefined,
    metadata: { source: 'dataset_review_regenerate' },
  });

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const item = (await MediaAPI.getMediaItem(response.media_id)) as Record<string, unknown>;
    const fileUrl = item?.file_url ?? item?.url;
    const failed =
      (typeof fileUrl === 'string' && fileUrl.includes('_failed')) || item?.status === 'failed';
    if (failed) {
      throw new Error(mediaFailureReason(item));
    }
    if (isReadyFileUrl(fileUrl)) {
      return { media_id: response.media_id, url: fileUrl };
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }
  throw new Error('Replacement image is still generating — check back shortly.');
};
